import React, {useContext, useRef} from "react";
import {Button, Form, Space, Typography} from "@bezlimit/bezlimit-ui";
import {useTranslation} from "react-i18next";
import {observer} from "mobx-react";
import _ from "lodash";
import "./style.scss";
// core
import {IPersonalDataRequest} from "../../../core/models/User";
import {InterceptorContext, LayoutContext, ModalContext} from "../../../core/Contexts";
import {getFieldsErrorSize} from "../../../core/functions/form";
// services
import UserService from "../../../services/UserService";
// hooks
import useFormHandler from "../../../core/hooks/useFormHandler";
import useFieldFocus from "../../../core/hooks/useFieldFocus";
import useOnCancel from "../../../core/hooks/useOnCancel";
import useOnChange from "../../../core/hooks/useOnChange";
// components
import PhoneField from "../../fields/phone";
import Error from "../../modal/error";
import CodeConfirmation from "../../modal/codeConfirmation";
import Success from "../../modal/success";
import BonusesService from "../../../services/BonusesService";

export default React.memo(
    observer(function TenForBezlimitForm() {
        const context = useContext(InterceptorContext);
        const contextLayout = useContext(LayoutContext);
        const contextModal = useContext(ModalContext);
        const ref = useRef(null);
        const {t} = useTranslation();
        const [form] = Form.useForm();
        const formInstance = useFormHandler(form);
        const onCancel = useOnCancel(formInstance);
        const onChange = useOnChange(formInstance);
        useFieldFocus(ref, "phone");

        const onSubmit = async ({code, phone}: any) => {
            await UserService.putPersonalData({code, phone});
            contextModal?.setChildren(
                <Success
                    title={<Typography as="span">{t("Номер подтвержден")}</Typography>}
                    subtitle={
                        <Typography as="span">
                            {t("Если вы указали номер Безлимит, то получите 10% в виде")}
                            <br />
                            {t("бонусов. Для смены номера обратитесь в поддержку")}
                        </Typography>
                    }
                    phone={phone}
                />
            );
            await BonusesService.summary({loading: false, loadingBlock: true, tag: "update"});
        };

        const onFinish = async (values: IPersonalDataRequest) => {
            const phone = _.replace(values.phone!, /(\+7|\s|-)/gi, "");
            try {
                await UserService.putPersonalData({phone});
                contextModal?.setChildren(<CodeConfirmation phone={phone} onSubmit={onSubmit} />);
                formInstance.resetFields();
            } catch (e: any) {
                contextModal?.setChildren(<Error phone={phone} />);
            }
        };

        return (
            <div ref={ref}>
                <Typography as="div" className="bl-ten-for-bezlimit-form">
                    <Form
                        form={formInstance}
                        layout="horizontal"
                        name="ten-for-bezlimit"
                        onFinish={onFinish}
                        onFieldsChange={onChange}
                        autoComplete="off"
                        initialValues={{
                            phone: ""
                        }}
                    >
                        <Space
                            size={_.gte(contextLayout?.width ?? 0, 768) ? 8 : 24}
                            direction="vertical"
                        >
                            <PhoneField />
                            <Form.Item noStyle shouldUpdate>
                                {({getFieldsValue, getFieldsError}) => {
                                    const {phone} = getFieldsValue();
                                    const errors = getFieldsErrorSize(getFieldsError);
                                    const formIsComplete =
                                        !!phone &&
                                        !(_.replace(phone!, /(\+7|\s|-)/gi, "").length < 10) &&
                                        !errors;

                                    return (
                                        <Button
                                            type={!formIsComplete ? "default" : "primary"}
                                            htmlType="submit"
                                            disabled={!formIsComplete}
                                            loading={context?.loadingBtn}
                                        >
                                            {t("Зарегистрировать")}
                                        </Button>
                                    );
                                }}
                            </Form.Item>
                            {_.lte(contextLayout?.width ?? 0, 768) && (
                                <Typography
                                    as="div"
                                    className="text-center cancel"
                                    size={14}
                                    color="#000"
                                >
                                    <Button type="text" onClick={onCancel}>
                                        {t("Отменить")}
                                    </Button>
                                </Typography>
                            )}
                        </Space>
                    </Form>
                </Typography>
            </div>
        );
    })
);
